import { type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const statusDotVariants = cva(
  "shrink-0 rounded-full",
  {
    variants: {
      status: {
        connected: "bg-intri-success",
        disconnected: "bg-intri-muted",
        error: "bg-intri-error",
        warning: "bg-intri-warning",
        busy: "bg-intri-accent",
        idle: "bg-intri-faint",
      },
      size: {
        sm: "h-1.5 w-1.5",
        default: "h-2 w-2",
        lg: "h-2.5 w-2.5",
      },
      pulse: {
        true: "animate-pulse-soft",
        false: "",
      },
    },
    defaultVariants: {
      status: "disconnected",
      size: "default",
      pulse: false,
    },
  },
);

export interface StatusDotProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusDotVariants> {
  /** Optional label shown next to the dot */
  label?: string;
  /** Label visibility on mobile */
  labelHidden?: boolean;
}

export function StatusDot({
  className,
  status,
  size,
  pulse,
  label,
  labelHidden = true,
  ...props
}: StatusDotProps) {
  if (!label) {
    return (
      <div
        className={cn(statusDotVariants({ status, size, pulse, className }))}
        {...props}
      />
    );
  }

  // Derive label color from status
  const labelColor = {
    connected: "text-intri-success",
    disconnected: "text-intri-muted",
    error: "text-intri-error",
    warning: "text-intri-warning",
    busy: "text-intri-accent",
    idle: "text-intri-faint",
  }[status ?? "disconnected"];

  return (
    <div className={cn("flex items-center gap-1.5", className)} {...props}>
      <div className={cn(statusDotVariants({ status, size, pulse }))} />
      <span
        className={cn(
          "text-2xs",
          labelColor,
          labelHidden && "hidden sm:inline",
        )}
      >
        {label}
      </span>
    </div>
  );
}
